import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  Typography,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListSubheader,
} from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EventIcon from '@mui/icons-material/Event'
import ScheduleIcon from '@mui/icons-material/Schedule'
import ApartmentIcon from '@mui/icons-material/Apartment'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { toast } from 'react-toastify'
import { getCalendarToken, getFilesWithExpiryDates } from 'services/calendarService'
import { getTasks } from 'services/taskService'
import { fetchDashboardScopeOptions } from 'services/BpService'
import { PRIVATE_PATHS } from 'utils/paths'

const PERIOD_OPTIONS = [
  { value: 1, label: '1 mo' },
  { value: 3, label: '3 mo' },
  { value: 6, label: '6 mo' },
  { value: 12, label: '1 year' },
  { value: 24, label: '2 years' },
  { value: 36, label: '3 years' },
]

const CalendarSubscriptionPage = () => {
  const navigate = useNavigate()
  const [months, setMonths] = useState(12)
  const [scopeValue, setScopeValue] = useState('all')
  const [scopeOptions, setScopeOptions] = useState(null)
  const [taskEvents, setTaskEvents] = useState([])
  const [tokenData, setTokenData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)
  const [events, setEvents] = useState([])
  const [eventsLoading, setEventsLoading] = useState(false)
  const [eventsPage, setEventsPage] = useState(1)
  const [eventsTotalPages, setEventsTotalPages] = useState(1)
  const [eventsTotal, setEventsTotal] = useState(0)

  const fetchToken = useCallback(async (selectedMonths, scopeP = {}) => {
    setLoading(true)
    setError(null)
    try {
      const response = await getCalendarToken(false, null, [], selectedMonths, scopeP)
      setTokenData(response.data)
    } catch (e) {
      console.error('Calendar token fetch failed:', e)
      setError('Failed to load calendar data. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchEvents = useCallback(async (selectedMonths, page = 1) => {
    setEventsLoading(true)
    try {
      const today = new Date().toISOString().split('T')[0]
      const future = new Date()
      future.setMonth(future.getMonth() + selectedMonths)
      const to = future.toISOString().split('T')[0]
      const response = await getFilesWithExpiryDates(page, 15, null, 'all', null, today, to, [], false, false, true)
      const resData = response.data
      setEvents(resData?.data || [])
      const meta = resData?.meta || {}
      setEventsTotalPages(meta.lastPage || meta.last_page || 1)
      setEventsTotal(meta.total || 0)
      setEventsPage(page)
    } catch (e) {
      console.error('Events fetch failed:', e)
    } finally {
      setEventsLoading(false)
    }
  }, [])

  const fetchTaskEvents = useCallback(async (selectedMonths, scopeP = {}) => {
    try {
      const today = new Date().toISOString().split('T')[0]
      const future = new Date()
      future.setMonth(future.getMonth() + selectedMonths)
      const to = future.toISOString().split('T')[0]
      const res = await getTasks({
        'filter[deadline][from]': today,
        'filter[deadline][to]': to,
        sort: 'deadline',
        per_page: 30,
        ...(scopeP.portfolioId ? { 'filter[portfolio_id]': scopeP.portfolioId } : {}),
        ...(scopeP.estateId ? { 'filter[estate_id]': scopeP.estateId } : {}),
        ...(scopeP.buildingPassportId ? { 'filter[building_passport_id]': scopeP.buildingPassportId } : {}),
      })
      const list = res?.data?.data || res?.data || []
      setTaskEvents((Array.isArray(list) ? list : []).filter(t => t.status !== 'completed'))
    } catch (e) {
      setTaskEvents([])
    }
  }, [])

  useEffect(() => {
    fetchDashboardScopeOptions()
      .then(r => setScopeOptions(r?.data?.data || r?.data || null))
      .catch(() => {})
  }, [])

  const scopeParams = useMemo(() => {
    const [type, id] = scopeValue.split(':')
    if (type === 'portfolio') return { portfolioId: id }
    if (type === 'estate') return { estateId: id }
    if (type === 'bp') return { buildingPassportId: id }
    return {}
  }, [scopeValue])

  const scopeQuery = useMemo(() => {
    const [type, id] = scopeValue.split(':')
    if (type === 'portfolio') return `&portfolio_id=${id}`
    if (type === 'estate') return `&estate_id=${id}`
    if (type === 'bp') return `&bp=${id}`
    return ''
  }, [scopeValue])

  useEffect(() => {
    fetchToken(months, scopeParams)
    fetchEvents(months, 1)
    fetchTaskEvents(months, scopeParams)
  }, [months, scopeParams, fetchToken, fetchEvents, fetchTaskEvents])

  const apiBaseUrl = useMemo(() => {
    const origin = window.location.origin
    if (origin.includes('localhost:3000')) return origin.replace(':3000', ':8080')
    if (origin.includes('://app.')) return origin.replace('://app.', '://')
    return origin
  }, [])

  const feedUrl = tokenData?.token
    ? `${apiBaseUrl}/ical/${tokenData.token}?months=${months}${scopeQuery}`
    : ''
  const webcalUrl = feedUrl.replace(/^https?:\/\//, 'webcal://')
  const googleUrl = feedUrl ? `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}` : ''
  const outlookUrl = feedUrl ? `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(webcalUrl)}` : ''

  const handleCopy = () => {
    if (!feedUrl) return
    navigator.clipboard.writeText(feedUrl)
    setCopied(true)
    toast.success('Calendar URL copied to clipboard')
    setTimeout(() => setCopied(false), 3000)
  }

  const eventCount = tokenData?.eventCount ?? 0
  const periodLabel = PERIOD_OPTIONS.find(o => o.value === months)?.label || `${months} months`

  return (
    <PageWrapper>
      <PageContainer>
        <BackLink onClick={() => navigate(PRIVATE_PATHS.calendar)}>
          <ArrowBackIcon fontSize='small' />
          Back to Calendar
        </BackLink>

        <HeroBar>
          <CalendarMonthIcon style={{ fontSize: 24, color: '#1a8a96' }} />
          <div>
            <HeroTitle>Renewal Calendar Subscription</HeroTitle>
            <HeroSubtitle>Get automatic reminders for upcoming renewals, inspections and task due dates in your calendar app.</HeroSubtitle>
          </div>
        </HeroBar>

        {/* How it works - full width at the top */}
        <InfoCard>
          <InfoHeader>
            <InfoOutlinedIcon fontSize='small' style={{ color: '#999' }} />
            How it works
          </InfoHeader>
          <InfoItem>Your calendar app checks this feed automatically for updates (every few hours).</InfoItem>
          <InfoItem>Google Calendar and Outlook may take up to 24 hours to show changes.</InfoItem>
          <InfoItem>Document renewal/inspection dates and task due dates are included, for the scope you choose.</InfoItem>
          <InfoItem>Only buildings you have access to appear. Superseded documents and completed tasks are excluded.</InfoItem>
          <InfoItem>To stop syncing, simply remove the subscription from your calendar app.</InfoItem>
        </InfoCard>

        <TwoColumnLayout>
          <MainColumn>
            {/* Events Preview */}
            <Card>
              <CardTitle>
                <EventIcon fontSize='small' style={{ color: '#1a8a96' }} />
                Events Preview
              </CardTitle>
              <CardDescription>These documents and tasks will appear in your calendar.</CardDescription>
              {eventsLoading ? (
                <LoadingState style={{ padding: '16px 0' }}>
                  <CircularProgress size={20} style={{ color: '#1a8a96' }} />
                </LoadingState>
              ) : events.length === 0 ? (
                <EmptyEvents>No upcoming renewals or inspections found for this period.</EmptyEvents>
              ) : (
                <EventsList>
                  {events.map(event => (
                    <EventRow key={event.id}>
                      <EventDate $type={event.dateType}>
                        {(event.expirationDate || event.nextInspectionDate || '').slice(0, 10)}
                      </EventDate>
                      <EventInfo>
                        <EventName>{event.displayName}</EventName>
                        <EventMeta>
                          {event.fileType && <span>{event.fileType}</span>}
                          {event.buildingPassportName && <span>{event.buildingPassportName}</span>}
                        </EventMeta>
                      </EventInfo>
                      <EventBadge $type={event.dateType}>
                        {event.dateType === 'expiry' ? 'Expires' : 'Inspection'}
                      </EventBadge>
                    </EventRow>
                  ))}
                  {eventsTotalPages > 1 && (
                    <PaginationRow>
                      <PaginationButton
                        disabled={eventsPage <= 1}
                        onClick={() => fetchEvents(months, eventsPage - 1)}
                      >
                        Previous
                      </PaginationButton>
                      <PaginationInfo>
                        Page {eventsPage} of {eventsTotalPages} ({eventsTotal} total)
                      </PaginationInfo>
                      <PaginationButton
                        disabled={eventsPage >= eventsTotalPages}
                        onClick={() => fetchEvents(months, eventsPage + 1)}
                      >
                        Next
                      </PaginationButton>
                    </PaginationRow>
                  )}
                </EventsList>
              )}
              {taskEvents.length > 0 && (
                <TaskGroup>
                  <TaskGroupTitle>Task due dates ({taskEvents.length})</TaskGroupTitle>
                  <EventsList>
                    {taskEvents.map(t => (
                      <EventRow key={`task-${t.id}`}>
                        <EventDate $type='task'>{(t.deadline || '').slice(0, 10)}</EventDate>
                        <EventInfo>
                          <EventName>{t.title || 'Task'}</EventName>
                          <EventMeta>
                            {t.building?.name && <span>{t.building.name}</span>}
                            {t.status && <span>{String(t.status).replace(/_/g, ' ')}</span>}
                          </EventMeta>
                        </EventInfo>
                        <EventBadge $type='task'>Task Due</EventBadge>
                      </EventRow>
                    ))}
                  </EventsList>
                </TaskGroup>
              )}
            </Card>

          </MainColumn>

          {/* Right Sidebar - Actionables */}
          <Sidebar>
            <SidebarCard>
              <SidebarTitle>Scope</SidebarTitle>
              <FormControl fullWidth size='small' data-tour-id='calendarSubscribe.scope'>
                <Select value={scopeValue} onChange={e => setScopeValue(e.target.value)}>
                  <MenuItem value='all'>All your Building Passports</MenuItem>
                  {scopeOptions?.portfolios?.length > 0 && <ListSubheader>Portfolios</ListSubheader>}
                  {(scopeOptions?.portfolios || []).map(p => (
                    <MenuItem key={`p-${p.id}`} value={`portfolio:${p.id}`}>{p.name}</MenuItem>
                  ))}
                  {scopeOptions?.estates?.length > 0 && <ListSubheader>Estates</ListSubheader>}
                  {(scopeOptions?.estates || []).map(es => (
                    <MenuItem key={`e-${es.id}`} value={`estate:${es.id}`}>{es.name}</MenuItem>
                  ))}
                  {scopeOptions?.buildingPassports?.length > 0 && <ListSubheader>Building Passports</ListSubheader>}
                  {(scopeOptions?.buildingPassports || []).map(b => (
                    <MenuItem key={`b-${b.id}`} value={`bp:${b.id}`}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ScopeNote>Only buildings you can access are included.</ScopeNote>
            </SidebarCard>

            {/* Period Selection */}
            <SidebarCard>
              <SidebarTitle>Time Period</SidebarTitle>
              <PeriodGrid>
                {PERIOD_OPTIONS.map(opt => (
                  <PeriodButton
                    key={opt.value}
                    $active={months === opt.value}
                    onClick={() => setMonths(opt.value)}
                  >
                    {opt.label}
                  </PeriodButton>
                ))}
              </PeriodGrid>
            </SidebarCard>

            {/* Stats */}
            <SidebarCard>
              <SidebarTitle>Summary</SidebarTitle>
              {loading ? (
                <LoadingState>
                  <CircularProgress size={20} style={{ color: '#1a8a96' }} />
                </LoadingState>
              ) : error ? (
                <ErrorState>{error}</ErrorState>
              ) : (
                <>
                  <StatRow>
                    <StatNumber>{eventCount}</StatNumber>
                    <StatLabel>event{eventCount !== 1 ? 's' : ''} in the next {periodLabel}</StatLabel>
                  </StatRow>
                  {tokenData?.scopeLabel && (
                    <ScopeChip><ApartmentIcon style={{ fontSize: 14 }} /> {tokenData.scopeLabel}</ScopeChip>
                  )}
                  <Divider />
                  <DetailRow>
                    <DetailIcon><EventIcon style={{ fontSize: 16, color: '#e65100' }} /></DetailIcon>
                    <DetailLabel>Expiry dates</DetailLabel>
                    <DetailValue>{tokenData?.expiryCount ?? 0}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailIcon><ScheduleIcon style={{ fontSize: 16, color: '#33becc' }} /></DetailIcon>
                    <DetailLabel>Inspections</DetailLabel>
                    <DetailValue>{tokenData?.inspectionCount ?? 0}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailIcon><EventIcon style={{ fontSize: 16, color: '#33becc' }} /></DetailIcon>
                    <DetailLabel>Task due dates</DetailLabel>
                    <DetailValue>{tokenData?.taskCount ?? 0}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailIcon><ApartmentIcon style={{ fontSize: 16, color: '#666' }} /></DetailIcon>
                    <DetailLabel>Building Passports</DetailLabel>
                    <DetailValue>{tokenData?.buildingCount ?? 0}</DetailValue>
                  </DetailRow>
                </>
              )}
            </SidebarCard>

            {/* Subscribe */}
            <SidebarCard>
              <SidebarTitle>Subscribe</SidebarTitle>
              <SubscribeGrid data-tour-id='calendarSubscribe.quick-add'>
                <SubscribeCard onClick={() => feedUrl && window.open(googleUrl, '_blank')} $disabled={!feedUrl}>
                  <SubscribeIconWrapper $color='#4285f4'>G</SubscribeIconWrapper>
                  <SubscribeName>Google</SubscribeName>
                </SubscribeCard>
                <SubscribeCard onClick={() => feedUrl && window.open(outlookUrl, '_blank')} $disabled={!feedUrl}>
                  <SubscribeIconWrapper $color='#0078d4'>O</SubscribeIconWrapper>
                  <SubscribeName>Outlook</SubscribeName>
                </SubscribeCard>
                <SubscribeCard onClick={() => feedUrl && window.open(webcalUrl)} $disabled={!feedUrl}>
                  <SubscribeIconWrapper $color='#333'>A</SubscribeIconWrapper>
                  <SubscribeName>Apple</SubscribeName>
                </SubscribeCard>
              </SubscribeGrid>
              <CopySection>
                <CopyLabel>Or copy feed URL:</CopyLabel>
                <CopyBox>
                  <CopyUrl>{feedUrl || 'Loading...'}</CopyUrl>
                  <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                    <CopyButton onClick={handleCopy} disabled={!feedUrl} data-tour-id='calendarSubscribe.copy'>
                      {copied ? <CheckCircleIcon style={{ color: '#4caf50', fontSize: 18 }} /> : <ContentCopyIcon style={{ fontSize: 18 }} />}
                    </CopyButton>
                  </Tooltip>
                </CopyBox>
              </CopySection>
            </SidebarCard>
          </Sidebar>
        </TwoColumnLayout>
      </PageContainer>
    </PageWrapper>
  )
}

// Layout
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`
const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 24px;
`
const BackLink = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 20px;
  &:hover { color: #1a8a96; }
`
const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  align-items: start;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`
const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`
const Sidebar = styled.div`
  position: sticky;
  top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

// Hero
const HeroBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #f0fafb 0%, #e8f5f3 100%);
  border-radius: 10px;
  border: 1px solid #d0e8e4;
`
const HeroTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1a3a3a;
`
const HeroSubtitle = styled.div`
  font-size: 13px;
  color: #5a7a7a;
  margin-top: 2px;
`

// Cards
const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 24px;
  border: 1px solid #e8edf2;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`
const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1a3a3a;
  margin-bottom: 6px;
`
const CardDescription = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0 0 16px;
`

// Period
const PeriodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
`
const PeriodButton = styled.button`
  padding: 10px 4px;
  border-radius: 8px;
  border: 2px solid ${({ $active }) => $active ? '#1a8a96' : '#e0e0e0'};
  background: ${({ $active }) => $active ? '#1a8a96' : '#fff'};
  color: ${({ $active }) => $active ? '#fff' : '#444'};
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? 600 : 500};
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: #1a8a96;
    background: ${({ $active }) => $active ? '#158085' : '#f0fafb'};
  }
`

// Subscribe
const SubscribeGrid = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`
const SubscribeCard = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 8px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.5 : 1};
  transition: all 0.15s;
  &:hover {
    border-color: ${({ $disabled }) => $disabled ? '#e0e0e0' : '#1a8a96'};
    box-shadow: ${({ $disabled }) => $disabled ? 'none' : '0 2px 6px rgba(26,138,150,0.12)'};
  }
`
const SubscribeIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ $color }) => $color};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
`
const SubscribeName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #333;
`

// Copy
const CopySection = styled.div`
  border-top: 1px solid #f0f0f0;
  padding-top: 16px;
`
const CopyLabel = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 6px;
`
const CopyBox = styled.div`
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 6px 8px 6px 12px;
`
const CopyUrl = styled.code`
  flex: 1;
  font-size: 11px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'SF Mono', Monaco, monospace;
`
const CopyButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  color: #666;
  &:hover { background: #eee; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`

// Events
const EmptyEvents = styled.div`
  font-size: 13px;
  color: #999;
  padding: 20px 0;
  text-align: center;
`
const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
`
const EventRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #fff;
`
const EventDate = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $type }) => $type === 'expiry' ? '#e65100' : $type === 'task' ? '#33becc' : '#33becc'};
  min-width: 80px;
  font-family: 'SF Mono', Monaco, monospace;
`
const TaskGroup = styled.div`
  margin-top: 16px;
`
const TaskGroupTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #33becc;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`
const EventInfo = styled.div`
  flex: 1;
  min-width: 0;
`
const EventName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const EventMeta = styled.div`
  font-size: 11px;
  color: #999;
  display: flex;
  gap: 8px;
  margin-top: 1px;
`
const EventBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ $type }) => $type === 'expiry' ? '#fff3e0' : $type === 'task' ? '#e8effd' : '#e6f7f9'};
  color: ${({ $type }) => $type === 'expiry' ? '#e65100' : $type === 'task' ? '#33becc' : '#33becc'};
  white-space: nowrap;
`
const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 12px;
  background: #fff;
`
const PaginationButton = styled.button`
  font-size: 12px;
  color: #1a8a96;
  background: none;
  border: 1px solid #d0e8e4;
  border-radius: 6px;
  padding: 4px 12px;
  cursor: pointer;
  &:disabled { color: #ccc; border-color: #eee; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #f0fafb; }
`
const PaginationInfo = styled.span`
  font-size: 12px;
  color: #999;
`

// Info
const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #888;
  margin-bottom: 10px;
`
const InfoCard = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 24px;
  border: 1px solid #e8edf2;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`
const InfoItem = styled.div`
  font-size: 13px;
  color: #666;
  line-height: 1.7;
  padding-left: 12px;
  position: relative;
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 9px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #ccc;
  }
`

// Sidebar
const SidebarCard = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 24px;
  border: 1px solid #e8edf2;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`
const SidebarTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a3a3a;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`
const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 0;
`
const LoadingText = styled.span`
  font-size: 13px;
  color: #999;
`
const ErrorState = styled.div`
  font-size: 13px;
  color: #d32f2f;
  padding: 16px 0;
  text-align: center;
`
const StatRow = styled.div`
  text-align: center;
  padding: 8px 0 16px;
`
const StatNumber = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #1a8a96;
  line-height: 1;
`
const StatLabel = styled.div`
  font-size: 13px;
  color: #888;
  margin-top: 4px;
`
const Divider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 12px 0;
`
const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`
const DetailIcon = styled.span`
  display: flex;
  align-items: center;
`
const DetailLabel = styled.span`
  flex: 1;
  font-size: 13px;
  color: #666;
`
const DetailValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`
const DateRange = styled.div`
  text-align: center;
  padding: 4px 0;
`
const DateRangeLabel = styled.div`
  font-size: 11px;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`
const DateRangeValue = styled.div`
  font-size: 13px;
  color: #555;
  margin-top: 2px;
`
const SidebarNote = styled.div`
  font-size: 11px;
  color: #bbb;
  margin-top: 16px;
  font-style: italic;
  text-align: center;
`
const ScopeNote = styled.div`
  font-size: 11px;
  color: #999;
  margin-top: 8px;
`
const ScopeChip = styled.div`
  width: fit-content;
  margin: 0 auto 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #f0fafb;
  border: 1px solid #d0e8e4;
  color: #1a6a72;
  font-size: 11.5px;
  font-weight: 600;
`

export default CalendarSubscriptionPage
